import React, { useContext, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button, Form, Modal, ProgressBar } from "react-bootstrap";
import { OrgContext } from "./PortalLayout";
import { listOrders, getOrder, uploadPo, confirmDelivery } from "../../services/portalService";
import { errorToast, successToast } from "../Toasts/Toasts";
import { statusClass, fmtDate, inr } from "./PortalRequirements";

// Orders: what was approved, what has been dispatched, what has arrived.
// The customer uploads a PO when their organization requires one, and
// confirms receipt at the end.

const CAN_PO = (user) => ["CUST_ADMIN", "CUST_APPROVER"].includes(user?.role);

export const OrderList = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { listOrders().then(setRows).catch(() => setRows([])); }, []);
  return (
    <>
      <h5 className="mb-2">Orders</h5>
      <div className="portal-card portal-list">
        {rows === null ? <div className="portal-empty">Loading…</div> : rows.length ? rows.map((o) => (
          <Link key={o.id} to={`/portal/orders/${o.id}`} className="item d-block text-decoration-none text-reset">
            <div className="d-flex justify-content-between">
              <span className="fw-semibold">{o.orderNumber}</span>
              <span className={`status-pill ${statusClass(o.status)}`}>{o.status}</span>
            </div>
            <div className="small text-muted">{o.itemCount} items · {inr(o.totals?.grand)}{o.deliveryDate ? ` · needed by ${fmtDate(o.deliveryDate)}` : ""}</div>
            <ProgressBar now={o.deliveredPct} label={o.deliveredPct ? `${o.deliveredPct}%` : ""} variant="success" style={{ height: 8 }} className="mt-1" />
          </Link>
        )) : <div className="portal-empty">No orders yet.</div>}
      </div>
    </>
  );
};

export const OrderDetail = () => {
  const { id } = useParams();
  const { user } = useContext(OrgContext);
  const [o, setO] = useState(null);
  const [poModal, setPoModal] = useState(false);
  const [po, setPo] = useState({ poNumber: "", poDate: "", file: null });
  const [confirmModal, setConfirmModal] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => getOrder(id).then(setO).catch((e) => errorToast(e?.message || "Not found"));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendPo = async (e) => {
    e.preventDefault();
    if (!po.file) return errorToast("Attach the PO file");
    const form = new FormData();
    form.append("file", po.file);
    if (po.poNumber) form.append("poNumber", po.poNumber);
    if (po.poDate) form.append("poDate", po.poDate);
    setBusy(true);
    try {
      const r = await uploadPo(id, form);
      if (r?.success) { successToast("PO uploaded"); setPoModal(false); load(); } else errorToast(r?.message);
    } catch (err) { errorToast(err?.message); } finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const r = await confirmDelivery(id, remarks);
      if (r?.success) { successToast("Thank you — receipt confirmed"); setConfirmModal(false); load(); } else errorToast(r?.message);
    } catch (err) { errorToast(err?.message); } finally { setBusy(false); }
  };

  if (!o) return <div className="portal-empty">Loading…</div>;
  const canConfirm = ["Dispatched", "Partially Delivered", "Delivered"].includes(o.status);
  const addr = o.deliveryAddress;

  return (
    <>
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div>
          <h5 className="mb-0">{o.orderNumber}</h5>
          <div className="small text-muted">From <Link to={`/portal/requirements/${o.requirementNumber}`}>{o.requirementNumber}</Link>{o.deliveryDate ? ` · needed by ${fmtDate(o.deliveryDate)}` : ""}</div>
        </div>
        <span className={`status-pill ${statusClass(o.status)}`}>{o.status}</span>
      </div>

      {o.status === "Awaiting PO" && (CAN_PO(user)
        ? <Button variant="success" className="w-100 mb-3" onClick={() => setPoModal(true)}>Upload purchase order</Button>
        : <div className="portal-card small text-muted">Waiting for your organization's purchase order — an approver or admin uploads it.</div>)}
      {canConfirm && <Button variant="success" className="w-100 mb-3" onClick={() => setConfirmModal(true)}>Confirm goods received</Button>}

      <div className="portal-card">
        <h6>Items</h6>
        <div className="table-responsive">
          <table className="table table-sm mb-0">
            <thead><tr><th>Item</th><th className="text-end">Ordered</th><th className="text-end">Delivered</th><th className="text-end">Amount</th></tr></thead>
            <tbody>
              {o.items.map((it) => (
                <tr key={it.id}>
                  <td>{it.item}{it.size ? <div className="small text-muted">{it.size}</div> : null}</td>
                  <td className="text-end">{Number(it.quantity)} {it.unit || ""}</td>
                  <td className="text-end">{Number(it.deliveredQty)}</td>
                  <td className="text-end">{inr(it.amount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr className="fw-bold"><td colSpan={3} className="text-end">Total incl. GST</td><td className="text-end">{inr(o.totals?.grand)}</td></tr></tfoot>
          </table>
        </div>
        {o.customerPoNumber && <div className="small mt-2">PO {o.customerPoNumber}{o.customerPoDate ? ` dated ${fmtDate(o.customerPoDate)}` : ""}</div>}
        {addr && <div className="small text-muted mt-1">Deliver to: {[addr.line1, addr.city, addr.pincode].filter(Boolean).join(", ")}</div>}
      </div>

      <div className="portal-card">
        <h6>Dispatches</h6>
        {o.dispatches.length ? o.dispatches.map((d) => (
          <div className="item py-2 border-bottom" key={d.id}>
            <div className="fw-semibold">Dispatch {d.dispatchNo} · {fmtDate(d.dispatchedAt)}</div>
            <div className="small text-muted">{[d.vehicle, d.driverName, d.driverPhone && `+${d.driverPhone}`, d.challanNumber && `Challan ${d.challanNumber}`].filter(Boolean).join(" · ")}</div>
            <div className="small">{d.items.map((it) => { const oi = o.items.find((x) => x.id === it.orderItemId); return oi ? `${oi.item} × ${it.quantity}` : `× ${it.quantity}`; }).join(", ")}</div>
            {d.notes && <div className="small text-muted">{d.notes}</div>}
          </div>
        )) : <div className="small text-muted">Nothing dispatched yet.</div>}
      </div>

      {o.documents?.length > 0 && (
        <div className="portal-card">
          <h6>Documents</h6>
          {o.documents.map((doc) => <div key={doc.id}><a href={doc.url} target="_blank" rel="noreferrer">{doc.fileName}</a> <span className="small text-muted">({doc.kind})</span></div>)}
        </div>
      )}
      {o.customerConfirmedAt && <div className="portal-card small">Receipt confirmed on {fmtDate(o.customerConfirmedAt)}{o.customerRemarks ? ` — "${o.customerRemarks}"` : ""}</div>}

      <Modal show={poModal} onHide={() => setPoModal(false)}>
        <Form onSubmit={sendPo}>
          <Modal.Header closeButton><Modal.Title>Upload purchase order</Modal.Title></Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-2"><Form.Label>PO number</Form.Label><Form.Control value={po.poNumber} onChange={(e) => setPo({ ...po, poNumber: e.target.value })} /></Form.Group>
            <Form.Group className="mb-2"><Form.Label>PO date</Form.Label><Form.Control type="date" value={po.poDate} onChange={(e) => setPo({ ...po, poDate: e.target.value })} /></Form.Group>
            <Form.Group><Form.Label>File (PDF or photo)</Form.Label><Form.Control type="file" accept="image/*,.pdf" onChange={(e) => setPo({ ...po, file: e.target.files[0] })} /></Form.Group>
          </Modal.Body>
          <Modal.Footer><Button type="submit" variant="success" disabled={busy}>Upload</Button></Modal.Footer>
        </Form>
      </Modal>

      <Modal show={confirmModal} onHide={() => setConfirmModal(false)}>
        <Modal.Header closeButton><Modal.Title>Confirm goods received</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group><Form.Label>Remarks (optional)</Form.Label><Form.Control as="textarea" rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Condition, shortages, anything to note" /></Form.Group>
        </Modal.Body>
        <Modal.Footer><Button variant="success" disabled={busy} onClick={confirm}>Confirm</Button></Modal.Footer>
      </Modal>
    </>
  );
};
